import { ChangeDetectionStrategy, Component, DestroyRef, inject, Input, ViewChild } from '@angular/core';
import { CampaignWithPreviewReportingDTO } from '../../../models/campaigns.model';
import { UtilService } from '../../../services/util.service';
import { CampaignReportingService } from '../../../services/campaign-reporting.service';
import { API, CampaignWithPreviewDTO } from '../../../services/api/api';
import { ErrorService } from '../../../services/error.service';
import { LineSmallChartComponent } from '@ngx-seatback/charts';
import { PermissionService } from '../../../services/permission.service';
import { SbSplitButtonItem, SearchOptions } from '@ngx-seatback/controls';
import { FilterPanelConfig } from '@ngx-seatback/layout';
import { StringTransformationService } from '../../../services/transformation-helper/string-transformation.service';
import { SbSplitButton } from '../../../services/global/global-interfaces';
import { RoutingID } from '../../../routes/sb-routing-enum';
import { finalize, take } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RequestClass } from '../../../services/global/RequestClass';

type SplitButtonAction = 'details';

@Component({
  selector: 'sb-comp-embedded-campaigns',
  templateUrl: './sb-embedded-campaigns.component.html',
  styleUrls: ['./sb-embedded-campaigns.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SbEmbeddedCampaignsComponent extends RequestClass {
  public readonly util = inject(UtilService);
  public readonly permissionService = inject(PermissionService);
  private readonly stringTransformation = inject(StringTransformationService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly campaignsService = inject(CampaignReportingService);
  private readonly api = inject(API);
  private readonly errorHandler = inject(ErrorService);

  @ViewChild('lineChart') lineChart: LineSmallChartComponent;

  @Input() resolveCampaignData: boolean;

  @Input() set campaigns(val: CampaignWithPreviewReportingDTO[]) {
    if (!this.permissionService.hasPermission(this.permissionService.Permission.ds_cm_reporting)) {
      val = val as CampaignWithPreviewDTO[];
    }
    if (this.resolveCampaignData) {
      if (!this.campaignIds) {
        this.campaignIds = [];
        for (const c of val) {
          this.campaignIds.push(c.id);
        }
        // this.requestDebounced();
      }
    }
  }

  get campaigns(): CampaignWithPreviewReportingDTO[] {
    return this._campaigns;
  }

  // table wrapper variables
  // tslint:disable-next-line:variable-name
  _campaigns: CampaignWithPreviewReportingDTO[];
  campaignIds: number[] = undefined;

  // filter variables
  exactMatch: SearchOptions = 'contains';
  search: string | null = null;
  filterConfig: FilterPanelConfig = {
    showExactMatch: true,
    showSearchHeadline: true,
    hideTagSection: true,
    button: { visibleWithoutChanges: true },
  };

  refreshFromServer() {
    if (this.campaignIds.length === 0 && !this.pagination) {
      this.loading.set(false);
      return;
    }
    const sub = this.api
      .ds_cm_Campaign_retrieveAllFrontendEnhanced(
        {
          search: this.stringTransformation.encodeSearchQueryParamWithOptions(this.exactMatch, this.search),
          id: this.campaignIds,
        },
        this.pagination
      )
      .pipe(
        take(1),
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.loading.set(false);
        })
      )
      .subscribe({
        next: data => {
          if (this.permissionService.hasPermission(this.permissionService.Permission.ds_cm_reporting)) {
            this._campaigns = this.campaignsService.getAndMergePulseChart(data);
          } else {
            this._campaigns = data;
          }
          this._campaigns = this.buildSplitButtons(this._campaigns);
        },
        error: error => {
          this.errorHandler.errorHandler(error);
        },
      });
    this.subs.push(sub);
  }

  buildSplitButtons<T extends { id: any }>(itemsToAdd: T[]): (T & SbSplitButton<SplitButtonAction>)[] {
    const result = itemsToAdd.map(item => {
      return { ...item, sbButtons: [] as SbSplitButtonItem<SplitButtonAction>[] };
    });
    result.forEach(item => {
      item.sbButtons.push({
        icon: 'details',
        tooltip: 'lang.globals.openDetails',
        link: RoutingID.commercials,
        pathParams: [{ id: 'campaignID', value: item?.id }],
      });
    });
    return result;
  }

  applyFiltering(filterMap: Map<string, any[]>) {
    this.setRequestOptions(filterMap);
    this.requestDebounced();
  }

  setRequestOptions(filterMap: Map<string, any[]>) {
    this.search = filterMap.get('search')?.[0];
    this.exactMatch = filterMap.get('exactMatch')?.[0];
  }
}
